<template>
  <div class="amenities-container">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                attach=".amenities-container"
                @cancel="closeHelp">
      <market-rent-amenities-help :openSection="openHelpSection"></market-rent-amenities-help>
    </res-dialog>
    <clear-all-dialog v-if="showClearResetDialog"
                      :showDialog="showClearResetDialog"
                      :maxWidth="480"
                      :setWidth="480"
                      @confirm="confirmClearResetDialog"
                      @cancel="closeClearResetDialog">
    </clear-all-dialog>
    <v-row no-gutters justify="space-between" class="grid-controls">
      <v-col md="auto" class="add-columns-controls">
        <v-row class="function-controls" align="center" no-gutters>
          <v-col md="auto"><span class="add-columns-label">Add Columns</span></v-col>
          <v-col md="auto">
            <v-checkbox class="add-columns-option"
                        color="var(--v-resBlue-darken1)"
                        label="Last Month (Prior Year)"
                        v-model="showLastMonthPriorYrColumn"
                        @change="toggleColumnVisibilities">
            </v-checkbox>
          </v-col>
          <v-col md="auto">
            <v-checkbox class="add-columns-option"
                        color="var(--v-resBlue-darken1)"
                        label="Totals (Prior Year)"
                        v-model="showTotalsPriorYrColumn"
                        @change="toggleColumnVisibilities">
            </v-checkbox>
          </v-col>
          <v-col md="auto">
            <v-checkbox class="add-columns-option"
                        color="var(--v-resBlue-darken1)"
                        label="YoY Variance $"
                        v-model="showVarianceAmountColumn"
                        @change="toggleColumnVisibilities">
            </v-checkbox>
          </v-col>
          <v-col md="auto">
            <v-checkbox class="add-columns-option"
                        color="var(--v-resBlue-darken1)"
                        label="YoY Variance %"
                        v-model="showVariancePercentageColumn"
                        @change="toggleColumnVisibilities">
            </v-checkbox>
          </v-col>
        </v-row>
      </v-col>
      <v-col md="auto">
        <div class="help-controls">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="openHelp(null)" class="pb-2" color="var(--v-resBlue-darken1)">mdi-help-circle</v-icon>
            </template>
            <span>Help</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" class="ml-3 mb-2" :color="commentIconColor" @click="openComments">{{ commentIcon  }}</v-icon>
            </template>
            <span>Comments/Flags</span>
          </v-tooltip>
          <v-menu left offset-y close-on-click @input="updateMenuState">
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon :color="menuColor" class="ellipses-control">
                <v-icon dense>{{menuIcon}}</v-icon>
              </v-btn>
            </template>
            <v-list class="ellipses-control-option-list">
              <v-list-item @click="openComments">
                <v-list-item-title class="ellipses-control-option">Add Flag or Comment</v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item v-bind="attrs" v-on="on" @click="clearAndResetPrompt">
                    <v-list-item-title class="ellipses-control-option">Clear & Reset</v-list-item-title>
                  </v-list-item>
                </template>
                <span>This will clear this tab of any entered information and revert to the initial values</span>
              </v-tooltip>
            </v-list>
          </v-menu>
        </div>
      </v-col>
    </v-row>
    <div class="grid-container" :style="gridContainerStyle">
      <ag-grid-vue style="width: 100%; height: 100%;"
                   class="ag-theme-material"
                   :gridOptions="gridOptions"
                   :columnDefs="currentHeaders"
                   :rowData="currentData"
                   :headerHeight="0"
                   :isExternalFilterPresent="shouldRowsBeFiltered"
                   :doesExternalFilterPass="shouldShowRow"
                   @cell-double-clicked="onCellDoubleClicked"
                   @cell-value-changed="onCellValueChanged">
      </ag-grid-vue>
    </div>
  </div>
</template>

<script>
  import YearGrid from '@/mixins/YearGrid';
  import HelpableMixin from '@/mixins/Helpable.js';
  import ClearAllDialog from "@/components/budgets/dialogs/ClearAllDialog";
  import ResDialog from '@/components/base/ResDialog.vue';

  import { AgGridVue } from "ag-grid-vue";
  import GroupedRowButton from '@/components/base/ag-grid/GroupedRowButton';
  import LeaseExpiresReadonlyCell from '@/components/base/ag-grid/LeaseExpiresReadonlyCell';
  import AveragedReadonlyCell from '@/components/base/ag-grid/AveragedReadonlyCell';
  import MarketRentAmenitiesHelp from '@/components/help/MarketRentAmenitiesHelp.vue';

  import budgetService from '@/services/budget';
  import marketRentService from '@/services/market-rent';
  import worksheetService from '@/services/worksheet';
  import budgetGlAccountservice from '@/services/budget-gl-account';
  import { mapGetters, mapActions } from 'vuex';

  const CHANGE_AMOUNT = 'change-amount';
  const UNIT_TYPE_HEADER = 'unit-type-header';
  const UNIT_TYPE = 'unit-type';
  const UNIT_HEADER = 'unit-header';
  const UNIT = 'unit';
  const TOTAL_COL_ID = 'total';

  export default {
    name: 'Amenities',
    mixins: [
      YearGrid,
      HelpableMixin
    ],
    components: {
      AgGridVue,
      ClearAllDialog,
      MarketRentAmenitiesHelp,
      ResDialog,
      /* eslint-disable vue/no-unused-components */
      GroupedRowButton, // used by AG Grid
      LeaseExpiresReadonlyCell,
      AveragedReadonlyCell,
      /* eslint-enable vue/no-unused-components */
    },
    props: {
      budgetIterationId: {
        required: true
      },
      readonly: {
        type: Boolean,
        default: false
      },
      commentIteration: {}
    },
    data: function () {
      return {
        CHANGE_AMOUNT: CHANGE_AMOUNT,
        UNIT_TYPE_HEADER: UNIT_TYPE_HEADER,
        UNIT_TYPE: UNIT_TYPE,
        UNIT_HEADER: UNIT_HEADER,
        UNIT: UNIT,
        PERIOD_INDEX_00_AMOUNT: this.getColId(0),
        PERIOD_INDEX_01_AMOUNT: this.getColId(1),
        PERIOD_INDEX_02_AMOUNT: this.getColId(2),
        PERIOD_INDEX_03_AMOUNT: this.getColId(3),
        PERIOD_INDEX_04_AMOUNT: this.getColId(4),
        PERIOD_INDEX_05_AMOUNT: this.getColId(5),
        PERIOD_INDEX_06_AMOUNT: this.getColId(6),
        PERIOD_INDEX_07_AMOUNT: this.getColId(7),
        PERIOD_INDEX_08_AMOUNT: this.getColId(8),
        PERIOD_INDEX_09_AMOUNT: this.getColId(9),
        PERIOD_INDEX_10_AMOUNT: this.getColId(10),
        PERIOD_INDEX_11_AMOUNT: this.getColId(11),
        TOTAL_COL_ID: TOTAL_COL_ID,
        fiscalYear: null,
        fiscalYearStartMonth: null,
        fiscalYearHeaders: [],
        budgetIterationLastModifiedTimestamp: null,
        summaryRefreshIsQueued: false,
        gridContainerHeight: 128,
        changeAmountRowId: 'change-amount-0',
        availableColumns: [],
        gridOptions: {
          headerHeight: 36,
          rowHeight: 36,
          domLayout: 'autoHeight',
          columnDefs: this.availableColumns,
          getRowNodeId: this.getRowNodeId,
          alignedGrids: [],
          suppressClickEdit: true,
          stopEditingWhenGridLosesFocus: true,
          frameworkComponents: {
            'GroupedRowButton': 'GroupedRowButton',
            'AveragedReadonlyCell': 'AveragedReadonlyCell'
          },
        },
        amenitiesData: [],
        showClearResetDialog: false,
        showLastMonthPriorYrColumn: false,
        showTotalsPriorYrColumn: false,
        showVarianceAmountColumn: false,
        showVariancePercentageColumn: false,
        tabHasUnresolvedComments: false,
        tabHasComments: false,
        menuOpen: false
      };
    },
    methods: {
      ...mapActions(['changeLoading', 'activateErrorDialog']),
      updateMenuState(isOpen) {
        this.menuOpen = isOpen;
      },
      async communicateEntriesUpdated() {
        var t = this;

        // refresh dependencies
        if (!t.summaryRefreshIsQueued) {
          t.summaryRefreshIsQueued = true;
          setTimeout(async () => {
            t.summaryRefreshIsQueued = false;
            t.$eventHub.$emit('amenityPremiumEntriesUpdated', t.budgetIterationId);
            // only updating GL mapping for these concessions, for now. Setting a timeout for the other concession entries is going to be different.
            var worksheetEntryTypeIds = [5, 6];
            /*   Enums.WorksheetEntryTypes.MonthlyAmenityChangeAmount,
                    Enums.WorksheetEntryTypes.AmenityAmount
                        */
            await budgetGlAccountservice.updateBasedOnWorksheets(t.budgetIterationId,
              worksheetEntryTypeIds, // List<byte>
              [], // List<Guid>
              t.budgetIterationLastModifiedTimestamp);
          }, 2500); // reduce refresh calls when multiple edits happen at once
        }
      },
      async load() {
        await this.loadGridConfiguration();
        await this.loadAmenitiesData();
      },
      async loadGridConfiguration() {
        let t = this;
        t.changeLoading(true);

        let settings = await budgetService.getBudgetSettings(t.budgetIterationId);
        t.fiscalYear = settings.year;
        t.fiscalYearStartMonth = settings.startMonth;
        t.budgetIterationLastModifiedTimestamp = settings.lastModifiedTimestamp;

        // setup column headers
        t.fiscalYearHeaders = t.getFiscalYearMonths(t.fiscalYearStartMonth);

        t.availableColumns = [];

        t.availableColumns.push({
          headerName: "", colId: "showUnitRecords", width: 16, pinned: 'left', suppressSizeToFit: true, cellStyle: { margin: 0, padding: 0 },
          field: "showUnitRecords",
          valueFormatter: function (params) {
            return t.rowIsHeader(params.data.rowType) && params.data.hasUnitRecords ? params.data.showUnitRecords : '';
          },
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellRendererSelector: t.selectGroupedCellRenderer,
        });
        t.availableColumns.push({
          headerName: "Unit Type", colId: "label", width: 96, pinned: 'left', suppressSizeToFit: true, suppressNavigable: true, field: "label",
          colSpan: function (params) {
            switch (params.data.rowType) {
              case CHANGE_AMOUNT:
                return 3;
              default:
                return 1;
            }
          },
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: function (params) {
            var style = { 'margin-right': 0, 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'center' };
            if (t.rowIsHeader(params.data.rowType)) {
              style['line-height'] = 'normal';
            }
            return style;
          },
          cellRenderer: function (params) {
            return t.rowIsHeader(params.data.rowType) ?
              `<span style="font-weight: bold">Unit</span>
               <br/>
               <span style="font-weight: bold">Type</span>` :
              `<span>${params.data.label}</span>`;
          }
        });
        t.availableColumns.push({
          headerName: "", colId: "subLabel", width: 60, pinned: 'left', suppressSizeToFit: true, suppressNavigable: true, field: "subLabel",
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: function (params) {
            var style = { 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'center' };
            if (t.rowIsHeader(params.data.rowType)) {
              style['line-height'] = 'normal';
            }
            return style;
          },
          cellRenderer: function (params) {
            return params.data.rowType == t.UNIT_TYPE_HEADER ?
              `<span style="font-weight: bold">No. of</span>
               <br/>
               <span style="font-weight: bold">Units</span>` :
              params.data.rowType == t.UNIT_HEADER ?
                `<span style="font-weight: bold">Unit</span>
               <br/>
               <span style="font-weight: bold">Number</span>` :
                `<span>${params.data.subLabel}</span>`;
          }
        });
        t.availableColumns.push({
          headerName: "", colId: "spacer", width: 60, pinned: 'left', suppressSizeToFit: true, suppressNavigable: true, field: "spacer",
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: { margin: 0, padding: 0 },
          cellRenderer: function () {
            return `<span> </span>`;
          }
        });
        t.availableColumns.push({
          headerName: "LAST NO. PRIOR YEAR", colId: "priorYearLastMonth", width: 96, pinned: 'left', suppressSizeToFit: true, suppressNavigable: true, field: "priorYearLastMonth", hide: !t.showLastMonthPriorYrColumn,
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: function (params) {
            var style = { 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'center' };
            if (t.rowIsHeader(params.data.rowType)) {
              style['line-height'] = 'normal';
            }
            return style;
          },
          cellRenderer: function (params) {
            return t.rowIsHeader(params.data.rowType) ?
              `<span style="font-weight: bold">LAST MO.</span>
               <br/>
               <span style="font-weight: normal">PRIOR YEAR</span>` :
              `<span>${params.data.rowType == t.UNIT || params.data.rowType == t.UNIT_TYPE ? t.formatCurrency(params.value, true, true) : '-' }</span>`;
          }
        });
        t.availableColumns.push({
          headerName: "TOTALS PRIOR YEAR", colId: "priorYearTotal", width: 125, pinned: 'left', suppressSizeToFit: true, suppressNavigable: true, field: "priorYearTotal", hide: !t.showTotalsPriorYrColumn,
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: function (params) {
            var style = { 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'center' };
            if (t.rowIsHeader(params.data.rowType)) {
              style['line-height'] = 'normal';
            }
            return style;
          },
          cellRenderer: function (params) {
            return t.rowIsHeader(params.data.rowType) ?
              `<span style="font-weight: bold">TOTALS</span>
               <br/>
               <span style="font-weight: normal">PRIOR YEAR</span>` :
              `<span>${params.data.rowType == t.UNIT || params.data.rowType == t.UNIT_TYPE ? t.formatCurrency(params.value, true, true) : '-' }</span>`;
          }
        });


        // months
        for (var i = 0; i < t.fiscalYearHeaders.length; i++) {
          t.availableColumns.push({
            headerName: t.fiscalYearHeaders[i], colId: t.getColId(i), width: 96, editable: t.isCellEditable, field: t.getColId(i),
            valueParser: t.currencyParser,
            valueGetter: t.entryGetter,
            valueFormatter: function (params) {
              return t.rowIsHeader(params.data.rowType) ? params.colDef.headerName : params.data.rowType == t.CHANGE_AMOUNT && params.value == null ? null :
                t.formatCurrency(params.value, true, true);
            },
            cellClass: function (params) {
              return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : '';
            },
            cellClassRules: {
              'readonly-column': function (params) {
                return !t.rowIsHeader(params.data.rowType) && !t.isCellEditable(params);
              }
            },
            cellStyle: function (params) {
              var style = { 'height': '100%' };
              if (t.rowIsHeader(params.data.rowType)) {
                style = style + ' font-weight: bold'
              }
              return style;
            },
            cellRendererSelector: t.selectPeriodCellRenderer,
          });
        }

        t.availableColumns.push({
          headerName: "TOTAL/AVG", colId: t.TOTAL_COL_ID, width: 96, pinned: 'right', suppressNavigable: true, field: t.TOTAL_COL_ID,
          valueGetter: function (params) {
            return params.data.rowType != t.UNIT ? t.calculateTotalUsingGetter(params)  : t.calculateTotalAsCurrency(params.data);
          },
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: function () {
            var style = { 'height': '100%' };
            return style;
          },
          cellRenderer: function (params) {
            return t.rowIsHeader(params.data.rowType) ?
              `<span style="font-weight: bold">TOTAL/AVG</span>` :
              `<span>${t.formatCurrency(params.value, true, true)}</span>`;
          }
        });
        t.availableColumns.push({
          headerName: "Var. $", colId: "varAmount", width: 96, pinned: 'right', suppressNavigable: true, field: "varAmount", hide: !t.showVarianceAmountColumn,
          valueGetter: function (params) {
            return (params.data.rowType == t.CHANGE_AMOUNT) ? null : t.calculateYoYVariance(params);
          },
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: function () {
            var style = { 'height': '100%' };
            return style;
          },
          cellRenderer: function (params) {
            return t.rowIsHeader(params.data.rowType) ?
              `<span style="font-weight: bold">Var. $</span>` :
              `<span>${params.data.rowType == t.CHANGE_AMOUNT ? '-' : t.formatCurrency(params.value, true, true) }</span>`;
          }
        });
        t.availableColumns.push({
          headerName: "Var. %", colId: "varPercent", width: 96, pinned: 'right', suppressNavigable: true, field: "varPercent", hide: !t.showVariancePercentageColumn,
          valueGetter: function (params) {
            return (params.data.rowType == t.CHANGE_AMOUNT) ?  null : t.calculateYoYVariancePercentage(params);
          },
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: function () {
            var style = { 'height': '100%' };
            return style;
          },
          cellRenderer: function (params) {
            return t.rowIsHeader(params.data.rowType) ?
              `<span style="font-weight: bold">Var. %</span>` :
              `<span>${params.data.rowType == t.CHANGE_AMOUNT ? '-' : t.percentageFormatter(params) }</span>`;
          }
        });
        t.changeLoading(false);
      },
      async loadAmenitiesData() {
        let t = this;
        t.changeLoading(true);
       
        var maxUnitCount = 0;
        
          var data = await marketRentService.getAmenitiesEntries(t.budgetIterationId);
        
        // update timestamp
        if (data.lastModifiedTimestamp) {
          t.budgetIterationLastModifiedTimestamp = data.lastModifiedTimestamp;
        }

        t.amenitiesData = [];
        t.amenitiesData.push(t.initRow(data.propertyLevelEntries, t.CHANGE_AMOUNT + '-' + t.changeAmountRowId, t.CHANGE_AMOUNT, t.changeAmountRowId, true, false));

        // use rowType to do conditional rendering of each row - header type gets it's own hand built cell components to make it look like a header and not be involved with the math side of things
        var unittypeheaderrow = {
          rowType: this.UNIT_TYPE_HEADER,
          rowId: 'unit-type-header-0',
          label: '',
          hasUnitRecords: false
        };
        t.amenitiesData.push(t.initRowHeader(unittypeheaderrow, unittypeheaderrow.rowId, false, false));

        data.unitTypeLevelEntries.forEach((ut) => {
          t.amenitiesData.push(t.initUnitTypeRow(ut, t.UNIT_TYPE + '-' + ut.unitTypeId, ut.unitTypeId, ut.unitLevelEntries.length > 0, false));

          // use rowType to do conditional rendering of each row - header type gets it's own hand built cell components to make it look like a header and not be involved with the math side of things
          var unitheaderrow = {
            rowType: this.UNIT_HEADER,
            rowId: 'unit-header-' + ut.unitTypeId,
            label: '',
            hasUnitRecords: false
          };
          t.amenitiesData.push(this.initRowHeader(unitheaderrow, this.UNIT_TYPE + '-' + ut.unitTypeId, true, false));

          ut.unitLevelEntries.forEach((unitChild) => {
            this.amenitiesData.push(this.initRow(unitChild, t.UNIT_TYPE + '-' + ut.unitTypeId, t.UNIT, unitChild.unitId, true, false));
          });

          if (maxUnitCount < ut.unitLevelEntries.length) { maxUnitCount = ut.unitLevelEntries.length; }          
        });

        var possibleHeight = (36 * data.unitTypeLevelEntries.length) + (36 * maxUnitCount) + 128;
        t.gridContainerHeight = possibleHeight > 668 ? 668 : possibleHeight; // set a minimum to keep the side scrolling within reasonable reach

        if (!t.anyOptionalColumnVisible) { 
          t.sizeGridColumns(t.gridOptions);
        }

        t.changeLoading(false);
      },
      initRowHeader(data, tag, isUnitHeader, showUnitRecords) {
        return {
          tag: tag,
          rowType: data.rowType,
          rowId: data.rowId,
          label: data.label,
          isEditable: false,

          // unit groupings
          hasUnitRecords: false,
          showUnitRecords: showUnitRecords,
          isUnitRecord: isUnitHeader
        }
      },
      initUnitTypeRow(data, tag, rowId, hasUnitRecords, showUnitRecords) {
        return {
          tag: tag,
          rowType: this.UNIT_TYPE,
          rowId: rowId,
          worksheetEntryTypeId: data.worksheetEntryTypeId,
          label: data.label,
          subLabel: data.subLabel,
          periodIndex00Amount: 0,
          periodIndex01Amount: 0,
          periodIndex02Amount: 0,
          periodIndex03Amount: 0,
          periodIndex04Amount: 0,
          periodIndex05Amount: 0,
          periodIndex06Amount: 0,
          periodIndex07Amount: 0,
          periodIndex08Amount: 0,
          periodIndex09Amount: 0,
          periodIndex10Amount: 0,
          periodIndex11Amount: 0,
          priorYearLastMonth: data.priorYearLastMonth,
          priorYearTotal: data.priorYearTotal,
          total: 0,
          varAmount: null,
          varPercent: null,
          isEditable: false,

          // unit groupings
          hasUnitRecords: hasUnitRecords,
          showUnitRecords: showUnitRecords,
          isUnitRecord: false
        }
      },
      initRow(data, tag, rowType, rowId, isEditable, showUnitRecords) {
        return {
          tag: tag,
          rowType: rowType,
          rowId: rowId,
          worksheetEntryTypeId: data.worksheetEntryTypeId,
          label: data.label,
          subLabel: data.subLabel,
          periodIndex00Amount: data.periodIndex00.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount == null && rowType != this.CHANGE_AMOUNT ? 0 : data.periodIndex11.amount,
          priorYearLastMonth: data.priorYearLastMonth,
          priorYearTotal: data.priorYearTotal,
          total: data.total,
          varAmount: data.VarianceAmount,
          varPercent: data.VariancePercentage,
          isEditable: isEditable,

          // unit groupings
          hasUnitRecords: false,
          showUnitRecords: showUnitRecords,
          isUnitRecord: rowType == this.UNIT
        }
      },
     

      clearAndResetPrompt() {
        if (this.readonly) {
          this.$emit('denied-status');
          return;
        }
        this.showClearResetDialog = true;
      },
      closeClearResetDialog() {
        this.showClearResetDialog = false;
      },
      async confirmClearResetDialog() {
        console.debug("clear and reset");
        var t = this;
        var response = await marketRentService.resetAmenitiesEntries(t.budgetIterationId, t.budgetIterationLastModifiedTimestamp);
        if (response) {
          await this.loadAmenitiesData();          
            await t.communicateEntriesUpdated();          
          t.$eventHub.$emit('showAlertNotification', 'Amenities entries cleared and reset successfully!');
        }
        else {
          t.activateErrorDialog({ title: 'Sync Failed', message: 'An error occurred clearing and resetting the data. Please try again.' });
        }
        this.showClearResetDialog = false;
        this.refreshDataFilter();
      },

      async onCellValueChanged(params) {
        var t = this;

        // double check these are data edit rows and columns that can be edited
        switch (params.colDef.colId) {
          case t.PERIOD_INDEX_00_AMOUNT:
          case t.PERIOD_INDEX_01_AMOUNT:
          case t.PERIOD_INDEX_02_AMOUNT:
          case t.PERIOD_INDEX_03_AMOUNT:
          case t.PERIOD_INDEX_04_AMOUNT:
          case t.PERIOD_INDEX_05_AMOUNT:
          case t.PERIOD_INDEX_06_AMOUNT:
          case t.PERIOD_INDEX_07_AMOUNT:
          case t.PERIOD_INDEX_08_AMOUNT:
          case t.PERIOD_INDEX_09_AMOUNT:
          case t.PERIOD_INDEX_10_AMOUNT:
          case t.PERIOD_INDEX_11_AMOUNT:
            t.changeLoading(true);

            console.debug("cell changed: " + params.data.rowType + ', ' + params.data.rowId + ', ' + params.colDef.colId + ', ' + params.newValue);

            var periodIndex = t.getPeriodIndex(params.colDef.colId);
            var calculateForPeriod = false;

            var itemsToUpdateGrid = [];

            var unitDatasToSave = [];

            switch (params.data.rowType) {
              case t.CHANGE_AMOUNT: // all data probably
                var propertyDatasToSave = [];
                var periodAmount;
                var nodeData;
                propertyDatasToSave.push(t.generatePropWorksheetEntriesFromData(params.data));
                itemsToUpdateGrid.push(params.data);

                // this is amount to change THEM ALL!!!!
                // run through units
                params.api.forEachNode(node => {
                  if (node.data.rowType == t.UNIT) {
                    nodeData = node.data;

                    for (var j = 0; j < 12; j++) {
                      calculateForPeriod = periodIndex <= j;

                      var n = t.parseCurrency(t.getPeriodAmount(j, params.data, false));
                      periodAmount = isNaN(n) ? 0 : n;

                      switch (j) {
                        case 0:
                          if (calculateForPeriod) {
                            nodeData.periodIndex00Amount = t.parseCurrency(nodeData.priorYearLastMonth) + periodAmount;
                          }
                          break;
                        case 1:
                          if (calculateForPeriod) {
                            nodeData.periodIndex01Amount = t.parseCurrency(nodeData.periodIndex00Amount) + periodAmount;
                          }
                          break;
                        case 2:
                          if (calculateForPeriod) {
                            nodeData.periodIndex02Amount = t.parseCurrency(nodeData.periodIndex01Amount) + periodAmount;
                          }
                          break;
                        case 3:
                          if (calculateForPeriod) {
                            nodeData.periodIndex03Amount = t.parseCurrency(nodeData.periodIndex02Amount) + periodAmount;
                          }
                          break;
                        case 4:
                          if (calculateForPeriod) {
                            nodeData.periodIndex04Amount = t.parseCurrency(nodeData.periodIndex03Amount) + periodAmount;
                          }
                          break;
                        case 5:
                          if (calculateForPeriod) {
                            nodeData.periodIndex05Amount = t.parseCurrency(nodeData.periodIndex04Amount) + periodAmount;
                          }
                          break;
                        case 6:
                          if (calculateForPeriod) {
                            nodeData.periodIndex06Amount = t.parseCurrency(nodeData.periodIndex05Amount) + periodAmount;
                          }
                          break;
                        case 7:
                          if (calculateForPeriod) {
                            nodeData.periodIndex07Amount = t.parseCurrency(nodeData.periodIndex06Amount) + periodAmount;
                          }
                          break;
                        case 8:
                          if (calculateForPeriod) {
                            nodeData.periodIndex08Amount = t.parseCurrency(nodeData.periodIndex07Amount) +  periodAmount;
                          }
                          break;
                        case 9:
                          if (calculateForPeriod) {
                            nodeData.periodIndex09Amount = t.parseCurrency(nodeData.periodIndex08Amount) + periodAmount;
                          }
                          break;
                        case 10:
                          if (calculateForPeriod) {
                            nodeData.periodIndex10Amount = t.parseCurrency(nodeData.periodIndex09Amount) + periodAmount;
                          }
                          break;
                        case 11:
                          if (calculateForPeriod) {
                            nodeData.periodIndex11Amount = t.parseCurrency(nodeData.periodIndex10Amount) + periodAmount;
                          }
                          break;
                        default:
                          break;
                      }
                    }
                    itemsToUpdateGrid.push(nodeData);
                    unitDatasToSave.push(t.generateUnitWorksheetEntriesFromData(nodeData));
                  }
                });

                t.gridOptions.api.applyTransaction({ update: itemsToUpdateGrid });

                // save property entries and update timestamp
                var propupdateresult1 = await worksheetService.updatePropertyWorksheetEntries(t.budgetIterationId,
                  propertyDatasToSave, t.budgetIterationLastModifiedTimestamp);
                if (propupdateresult1.lastModifiedTimestamp) {
                  t.budgetIterationLastModifiedTimestamp = propupdateresult1.lastModifiedTimestamp;
                }
                // save unit entries and update timestamp
                var updateUnitData1 = await worksheetService.updateUnitWorksheetEntries(t.budgetIterationId,
                  unitDatasToSave, t.budgetIterationLastModifiedTimestamp);
                // update timestamp
                if (updateUnitData1.lastModifiedTimestamp) {
                  t.budgetIterationLastModifiedTimestamp = updateUnitData1.lastModifiedTimestamp;
                }

              await t.communicateEntriesUpdated();
              break;
              case t.UNIT: // unit data changed only
                unitDatasToSave.push(t.generateUnitWorksheetEntriesFromData(params.data));
                var updateUnitData3 = await worksheetService.updateUnitWorksheetEntries(t.budgetIterationId,
                  unitDatasToSave, t.budgetIterationLastModifiedTimestamp);
                // update timestamp
                if (updateUnitData3.lastModifiedTimestamp) {
                  t.budgetIterationLastModifiedTimestamp = updateUnitData3.lastModifiedTimestamp;
                }
               await t.communicateEntriesUpdated();
                break;
              default:
                break;
            }


            t.changeLoading(false);
            break;
          default:
            break;
        }
      },
      generatePropWorksheetEntriesFromData(data) {
        return {
          worksheetEntryTypeId: data.worksheetEntryTypeId,
          periodIndex00: data.periodIndex00Amount,
          periodIndex01: data.periodIndex01Amount,
          periodIndex02: data.periodIndex02Amount,
          periodIndex03: data.periodIndex03Amount,
          periodIndex04: data.periodIndex04Amount,
          periodIndex05: data.periodIndex05Amount,
          periodIndex06: data.periodIndex06Amount,
          periodIndex07: data.periodIndex07Amount,
          periodIndex08: data.periodIndex08Amount,
          periodIndex09: data.periodIndex09Amount,
          periodIndex10: data.periodIndex10Amount,
          periodIndex11: data.periodIndex11Amount,
        };
      },
      generateUnitWorksheetEntriesFromData(data) {
        return {
          unitId: data.rowId,
          unitTypeId: this.getUnitTypeIdFromTag(data.tag),
          worksheetEntryTypeId: data.worksheetEntryTypeId,
          periodIndex00: data.periodIndex00Amount,
          periodIndex01: data.periodIndex01Amount,
          periodIndex02: data.periodIndex02Amount,
          periodIndex03: data.periodIndex03Amount,
          periodIndex04: data.periodIndex04Amount,
          periodIndex05: data.periodIndex05Amount,
          periodIndex06: data.periodIndex06Amount,
          periodIndex07: data.periodIndex07Amount,
          periodIndex08: data.periodIndex08Amount,
          periodIndex09: data.periodIndex09Amount,
          periodIndex10: data.periodIndex10Amount,
          periodIndex11: data.periodIndex11Amount,
        };
      },
      calculateTotalUsingGetter(node) {
        var t = this;
        let runningTotal = t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_00_AMOUNT, node)) +
          t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_01_AMOUNT, node)) +
          t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_02_AMOUNT, node)) +
          t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_03_AMOUNT, node)) +
          t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_04_AMOUNT, node)) +
          t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_05_AMOUNT, node)) +
          t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_06_AMOUNT, node)) +
          t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_07_AMOUNT, node)) +
          t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_08_AMOUNT, node)) +
          t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_09_AMOUNT, node)) +
          t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_10_AMOUNT, node)) +
          t.parseCurrency(t.gridOptions.api.getValue(t.PERIOD_INDEX_11_AMOUNT, node));
        return t.parseCurrency(runningTotal);
      },
      calculateAmountChange(previousAmount, currentAmount) {        
        var amount = currentAmount - previousAmount;
        if (amount == undefined || isNaN(amount)) { amount = null; }
        return amount;
      },
      calculateYoYVariance(params) {
        return this.parseCurrency(this.gridOptions.api.getValue(this.TOTAL_COL_ID, params) - params.data.priorYearTotal);
      },
      calculateYoYVariancePercentage(params) {
        var totalCol = this.gridOptions.api.getValue(this.TOTAL_COL_ID, params);
        if (totalCol != 0) {
          return this.parseCurrency(((totalCol - params.data.priorYearTotal) / totalCol) * 100);
        }
        return 0;
      },

      currencyParser(params) {
        var n = this.parseCurrency(params.newValue, false);
        if (isNaN(n)) { n = null; }
        return n;
      },
      entryGetter(params) {
        var t = this;
        //console.debug(params);

        var isAllSame = true;
        var tempTotal = 0;
        var count = 0;
        var change = 0;

        switch (params.colDef.colId) {
          case t.PERIOD_INDEX_00_AMOUNT:           
          case t.PERIOD_INDEX_01_AMOUNT:
          case t.PERIOD_INDEX_02_AMOUNT:
          case t.PERIOD_INDEX_03_AMOUNT:
          case t.PERIOD_INDEX_04_AMOUNT:
          case t.PERIOD_INDEX_05_AMOUNT:
          case t.PERIOD_INDEX_06_AMOUNT:
          case t.PERIOD_INDEX_07_AMOUNT:
          case t.PERIOD_INDEX_08_AMOUNT:
          case t.PERIOD_INDEX_09_AMOUNT:
          case t.PERIOD_INDEX_10_AMOUNT:
          case t.PERIOD_INDEX_11_AMOUNT:
            var periodIndex = t.getPeriodIndex(params.colDef.colId);
            var paramsAmount = t.getPeriodAmount(periodIndex, params.data, false);
            switch (params.data.rowType) {
              case t.CHANGE_AMOUNT:
                // if NOT ALL units have same amount change, get the AVERAGE
                params.api.forEachNode(node => {
                  if (paramsAmount != null && node.data.rowType == t.UNIT) { 
                    change = t.calculateAmountChange(t.getPeriodAmount(periodIndex, node.data, true), t.gridOptions.api.getValue(params.colDef.colId, node));
                    tempTotal = tempTotal + change;
                    count++;
                    if (isAllSame == true && change != paramsAmount) {
                      isAllSame = false;
                    }
                  }
                });
                return isAllSame ? paramsAmount : this.halfRoundUp(tempTotal / count, 2);
              case t.UNIT_TYPE:
                // get the total for all units of this type
                params.api.forEachNode(node => {
                  if (node.data.tag == params.data.tag && node.data.rowType == t.UNIT) {
                    tempTotal = tempTotal + t.parseCurrency(t.getPeriodAmount(periodIndex, node.data, false));
                  }
                });
                return t.halfRoundUp(tempTotal, 0);
              default:
                return paramsAmount;
            }
          default:
            return params.value;
        }
      },
      percentageFormatter(params) {
        return params.value != null ? params.value + '%' : '';
      },
      isPeriodIndexAmountColumn(field) {
        switch (field) {
          case this.PERIOD_INDEX_00_AMOUNT:
          case this.PERIOD_INDEX_01_AMOUNT:
          case this.PERIOD_INDEX_02_AMOUNT:
          case this.PERIOD_INDEX_03_AMOUNT:
          case this.PERIOD_INDEX_04_AMOUNT:
          case this.PERIOD_INDEX_05_AMOUNT:
          case this.PERIOD_INDEX_06_AMOUNT:
          case this.PERIOD_INDEX_07_AMOUNT:
          case this.PERIOD_INDEX_08_AMOUNT:
          case this.PERIOD_INDEX_09_AMOUNT:
          case this.PERIOD_INDEX_10_AMOUNT:
          case this.PERIOD_INDEX_11_AMOUNT:
            return true;
          default:
            return false;
        }
      },
      onCellDoubleClicked(params) {
        if (!this.isPeriodIndexAmountColumn(params.colDef.field)) {
          return;
        }
        params.api.startEditingCell({ rowIndex: params.rowIndex, colKey: params.colDef.field });
      },
      isCellEditable(params) {
        if (this.readonly) {
          return false;
        }
        var t = this;
        var isAllSame = true;
        if (params.data.isEditable) {
          switch (params.colDef.colId) {
            case t.PERIOD_INDEX_00_AMOUNT:
            case t.PERIOD_INDEX_01_AMOUNT:
            case t.PERIOD_INDEX_02_AMOUNT:
            case t.PERIOD_INDEX_03_AMOUNT:
            case t.PERIOD_INDEX_04_AMOUNT:
            case t.PERIOD_INDEX_05_AMOUNT:
            case t.PERIOD_INDEX_06_AMOUNT:
            case t.PERIOD_INDEX_07_AMOUNT:
            case t.PERIOD_INDEX_08_AMOUNT:
            case t.PERIOD_INDEX_09_AMOUNT:
            case t.PERIOD_INDEX_10_AMOUNT:
            case t.PERIOD_INDEX_11_AMOUNT:
              if (params.data.rowType == t.CHANGE_AMOUNT) {
                var periodIndex = t.getPeriodIndex(params.colDef.colId);
                var paramsAmount = t.getPeriodAmount(periodIndex, params.data, false);

                // if all units have same amount change, return true
                params.api.forEachNode(node => {
                  if (isAllSame == true && paramsAmount != null && node.data.rowType == t.UNIT
                    && t.calculateAmountChange(t.getPeriodAmount(periodIndex, node.data, true), t.gridOptions.api.getValue(params.colDef.colId, node)) != paramsAmount) {
                    isAllSame = false;
                  }
                });
                return isAllSame;
              } else {
                return true;
              }
            default:
              return false;
          }
        }
        return false;
      },
      rowIsHeader(rowType) {
        return rowType == this.UNIT_TYPE_HEADER || rowType == this.UNIT_HEADER;
      },
      selectGroupedCellRenderer(params) {
        let t = this;
        if (params.data.hasUnitRecords) {
          return {
            component: 'GroupedRowButton',
            params: {
              show: function () { return true; },
              allCollapsed: function (params) {
                return !params.data.showUnitRecords;
              },
              onClick: function (params) {
                let show = !params.data.showUnitRecords;
                params.api.forEachNode(node => {
                  if (node.data.tag == params.data.tag) {
                    node.setDataValue('showUnitRecords', show);
                  }
                });
                t.refreshDataFilter();
              },
            }
          }
        } else {
          return null;
        }
      },
      selectPeriodCellRenderer(params) {
        let t = this;
          if (params.data.rowType == t.CHANGE_AMOUNT && !t.isCellEditable(params)) {          
            return {
              component: 'AveragedReadonlyCell',
              params: {
                contents: params.value == null ? null : t.formatCurrency(params.value, true, true),
              }
            }
          } else {
            return null;
          }        
      },
      shouldRowsBeFiltered() {
        return true;
      },
      shouldShowRow(node) {
        return node.data.isUnitRecord == false ||
          node.data.showUnitRecords;
      },
      refreshDataFilter() {
        this.gridOptions.api.onFilterChanged();
      },
      getUnitTypeIdFromTag(tag) {
        const sub1 = this.UNIT_TYPE + '-';
        if (tag.indexOf(sub1) < 0) return false;
        var SP = tag.indexOf(sub1) + sub1.length;
        var indexString = tag.substring(SP, tag.length);
        return indexString;
      },
      getRowNodeId(data) {
        return data.rowType + "-" + data.rowId;
      },
      getColId(periodIndex) {
        return periodIndex < 10 ? "periodIndex0" + periodIndex + "Amount" : "periodIndex" + periodIndex + "Amount";
      },
      getPeriodIndex(colId) {
        const sub1 = 'periodIndex';
        const sub2 = 'Amount';
        if (colId.indexOf(sub1) < 0 || colId.indexOf(sub2) < 0) return false;
        var SP = colId.indexOf(sub1) + sub1.length;
        var string1 = colId.substr(0, SP);
        var string2 = colId.substr(SP);
        var TP = string1.length + string2.indexOf(sub2);
        var indexString = colId.substring(SP, TP);
        return Number(indexString);
      },
      getPeriodAmount(periodIndex, data, getPrevious) {
        switch (periodIndex) {
          case 1:
            return getPrevious ? data.periodIndex00Amount : data.periodIndex01Amount;
          case 2:
            return getPrevious ? data.periodIndex01Amount : data.periodIndex02Amount;
          case 3:
            return getPrevious ? data.periodIndex02Amount : data.periodIndex03Amount;
          case 4:
            return getPrevious ? data.periodIndex03Amount : data.periodIndex04Amount;
          case 5:
            return getPrevious ? data.periodIndex04Amount : data.periodIndex05Amount;
          case 6:
            return getPrevious ? data.periodIndex05Amount : data.periodIndex06Amount;
          case 7:
            return getPrevious ? data.periodIndex06Amount : data.periodIndex07Amount;
          case 8:
            return getPrevious ? data.periodIndex07Amount : data.periodIndex08Amount;
          case 9:
            return getPrevious ? data.periodIndex08Amount : data.periodIndex09Amount;
          case 10:
            return getPrevious ? data.periodIndex09Amount : data.periodIndex10Amount;
          case 11:
            return getPrevious ? data.periodIndex10Amount : data.periodIndex11Amount;
          case 0:
          default:
            return getPrevious ? data.priorYearLastMonth : data.periodIndex00Amount;
        }
      },
      toggleColumnVisibilities() {
        var t = this;
        var columnDefs = this.currentHeaders;
        columnDefs.forEach(function (colDef) {
          if (colDef.colId == 'priorYearLastMonth') { colDef.hide = !t.showLastMonthPriorYrColumn; }
          if (colDef.colId == 'priorYearTotal') { colDef.hide = !t.showTotalsPriorYrColumn; }
          if (colDef.colId == 'varAmount') { colDef.hide = !t.showVarianceAmountColumn; }
          if (colDef.colId == 'varPercent') { colDef.hide = !t.showVariancePercentageColumn; }

          if ((t.moreThanTwoOptionalColumnsVisible) && (colDef.colId == t.PERIOD_INDEX_00_AMOUNT || colDef.colId == t.PERIOD_INDEX_01_AMOUNT || colDef.colId == t.PERIOD_INDEX_02_AMOUNT
            || colDef.colId == t.PERIOD_INDEX_03_AMOUNT || colDef.colId == t.PERIOD_INDEX_04_AMOUNT || colDef.colId == t.PERIOD_INDEX_05_AMOUNT
            || colDef.colId == t.PERIOD_INDEX_06_AMOUNT || colDef.colId == t.PERIOD_INDEX_07_AMOUNT || colDef.colId == t.PERIOD_INDEX_08_AMOUNT
            || colDef.colId == t.PERIOD_INDEX_09_AMOUNT || colDef.colId == t.PERIOD_INDEX_10_AMOUNT || colDef.colId == t.PERIOD_INDEX_11_AMOUNT)
            || colDef.colId == t.TOTAL_COL_ID || colDef.colId == 'varAmount' || colDef.colId == 'varPercent') {
            colDef.width = 96;
          }
        });
        t.gridOptions.api.setColumnDefs(columnDefs);

        if (!t.moreThanTwoOptionalColumnsVisible) { 
          t.sizeGridColumns(t.gridOptions);
        }
      },
      openComments() {
        this.$emit('openComments', 'amenities');
      },
      async refreshCommentIcon() {
        var commentsResults = await worksheetService.commentsExist(this.budgetIterationId, 3);
        this.tabHasUnresolvedComments = commentsResults.unresolvedFlagExists;
        this.tabHasComments = commentsResults.hasComments;
        console.debug('refresh comment amenities');
      }
    },
    computed: {
      ...mapGetters(['currentLoading']),
      gridContainerStyle() {
        return 'height: ' + this.gridContainerHeight + 'px;';
      },
      currentData() {
        return this.amenitiesData;
      },
      currentHeaders() {
        return this.availableColumns;
      },
      anyOptionalColumnVisible() {
        return this.showLastMonthPriorYrColumn || this.showTotalsPriorYrColumn || this.showVarianceAmountColumn || this.showVariancePercentageColumn;
      },
      moreThanTwoOptionalColumnsVisible() {
        var count = (this.showLastMonthPriorYrColumn ? 1 : 0) + (this.showTotalsPriorYrColumn ? 1 : 0) + (this.showVarianceAmountColumn ? 1 : 0) + (this.showVariancePercentageColumn ? 1 : 0);
        return count > 2;
      },
      commentIconColor() {
        return this.tabHasUnresolvedComments ? 'var(--v-resRed-darken1)' : 'var(--v-resBlue-darken1)';
      },
      commentIcon() {
        return this.tabHasUnresolvedComments ? 'mdi-flag' : this.tabHasComments ? 'mdi-comment-text-multiple' : 'mdi-comment-multiple-outline';
      },
      menuIcon() {
        return this.menuOpen ? 'mdi-dots-horizontal-circle' : 'mdi-dots-horizontal';
      },
      menuColor() {
        return 'var(--v-resBlue-darken1)';
      }
    },
    watch: {
      currentLoading: function () {
        if (this.currentLoading == true) {
          this.showGridLoadingOverlay(this.gridOptions);
        } else {
          this.hideGridLoadingOverlay(this.gridOptions);
        }
      },
      commentIteration: function () {
        this.refreshCommentIcon();
      }
    },
    async mounted() {
      await this.load();
      await this.refreshCommentIcon();
    },
    async created() {
      this.$eventHub.$on('budgetSettingsUpdated', this.load);
    },
  };
</script>

<style lang="scss" scoped>
  .amenities-container {
    .v-input--selection-controls::v-deep {
      margin: 0 !important;
      padding: 0 !important;
    }

    .grid-controls {
      height: 40px;
      margin-bottom: 14px;
      margin-top: 10px;
    }

    .historicals-controls {
      display: inline-block;
      border: solid 1px var(--v-resGrey-lighten3);
      padding: 6px 0 0 10px;
      height: 100%;
    }

    .add-columns-controls {
      display: inline-block;
      border: solid 1px var(--v-resGrey-lighten3);
      vertical-align: top;
      height: 100%;
    }

    .add-columns-label {
      vertical-align: top;
      display: inline-block;
      padding: 7px 8px 20px 10px;
      height: 100%;
    }

    .add-columns-option::v-deep {
      display: inline-block;

      .v-label {
        font-size: 13px !important;
        color: var(--v-resGrey-darken2);
        font-weight: bold;
        padding-right: 14px;
        padding-top: 8px;
      }

      .v-input--selection-controls__input {
        margin-top: 0;
        padding-top: 8px;
      }
    }

    .help-controls {
      height: 100%;
      float: right;
      border: solid 1px var(--v-resGrey-lighten3);
      padding: 7px 6px 6px 6px;
      cursor: pointer !important;
    }

    .ellipses-control {
      height: 20px;
      width: 20px;
      margin-left: 10px;
      margin-bottom: 8px;
    }
    
    .ellipses-control-option-list {
      border-radius: 6px;
      border: 1px solid var(--v-resBlue-darken1);
    }
    
    .ellipses-control-option {
      font-size: 13px;
    }
  }
</style>
